import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import {
  getreviewedProperties,
  reviewRattings,
  deleteReviews,
  updateReviewStatus
} from 'src/ApiServices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewReviewModal from 'src/sections/ViewReviewModal';
import ClearIcon from '@mui/icons-material/Clear';

export default function ReviewRating() {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [reviews, setReviews] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewReview, setViewReview] = useState(null);

  // Ref to track if properties have been fetched
  const propertiesFetched = useRef(false);

  const handleSearchSubmit = () => {
    setSearch(tempSearch);
    setPage(1);
  };

  const filteredProperties = properties.filter((property) =>
    property?.title?.toLowerCase().includes(propertySearch.toLowerCase())
  );

  // Delete Confirmation Functions
  const handleDeleteClick = (review) => {
    setSelectedReview(review);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReview) return;

    try {
      setDeleteLoading(true);
      await deleteReviews(selectedReview.id);
      toast.success('Review deleted successfully!');
      fetchReviewsData(page);
      setDeleteModalOpen(false);
      setSelectedReview(null);
    } catch (error) {
      console.error('Failed to delete review:', error);
      if (error.response?.status === 404) {
        toast.error('Review not found!');
      } else if (error.response?.status === 403) {
        toast.error('Permission denied!');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized! Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to delete review!');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedReview(null);
  };

  // View Modal Functions
  const handleViewClick = (review) => {
    setViewReview(review);
    setViewModalOpen(true);
  };

  const handleViewClose = () => {
    setViewModalOpen(false);
    setViewReview(null);
  };
  const handleClearSearch = useCallback(async () => {
    setTempSearch('');
    setSearch('');
    setPage(1);
    await fetchReviewsData(1, '');
  }, []);

  const handleStatusToggle = async (review) => {
    try {
      let newStatus;
      if (review.status === 'active') {
        newStatus = 'inactive';
      } else {
        newStatus = 'active';
      }

      const updatedReviews = reviews.map((item) =>
        item.id === review.id ? { ...item, status: newStatus } : item
      );
      setReviews(updatedReviews);

      const payload = {
        review_id: review.id,
        status: newStatus === 'active' ? 2 : 1
      };

      // API call
      await updateReviewStatus(payload);
      toast.success(`Review ${newStatus === 'active' ? 'approved' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Failed to update review status:', error);

      // Revert optimistic update on error
      const originalReviews = reviews.map((item) =>
        item.id === review.id ? { ...item, status: review.status } : item
      );
      setReviews(originalReviews);

      toast.error(error.response?.data?.message || 'Failed to update review status!');
    }
  };

  const fetchProperties = async () => {
    if (propertiesFetched.current) return;
    propertiesFetched.current = true;

    try {
      const response = await getreviewedProperties();
      const propertiesArray = Array.isArray(response?.data) ? response.data : [];
      setProperties(propertiesArray);
      setPropertiesLoaded(true);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties([]);
      setPropertiesLoaded(true);
    }
  };

  const fetchReviewsData = async (currentPage) => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (search) params.search = search;
      // if (selectedProperty) params.property_id = selectedProperty;
      if (selectedProperty && selectedProperty !== 'all') {
        params.property_id = selectedProperty;
      }

      const response = await reviewRattings(params);

      if (response?.data && Array.isArray(response.data)) {
        setReviews(response.data);
        setPagination(
          response.pagination || {
            current_page: 1,
            per_page: response.data.length,
            total: response.data.length,
            last_page: 1
          }
        );
      } else {
        setReviews([]);
        setPagination({ current_page: 1, per_page: 20, total: 0, last_page: 1 });
      }
    } catch (error) {
      console.error('Failed to load reviews:', error);
      setReviews([]);
      setPagination({ current_page: 1, per_page: 20, total: 0, last_page: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedProperty) return;
    setPropertySearch('');
  }, [selectedProperty]);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (propertiesLoaded) {
      fetchReviewsData(page);
    }
  }, [page, search, selectedProperty, propertiesLoaded]);

  const tableHeaders = [
    'Property Details',
    'User Details',
    'Reviews',
    'Status',
    'Rating',
    'Posted',
    'Approve',
    'Actions'
  ];

  const stickyStyle = (left = true) => ({
    px: 2,
    position: { xs: 'relative', md: 'sticky' },
    [left ? 'left' : 'right']: { xs: 'auto', md: 0 },
    zIndex: { xs: 'auto', md: 1 },
    backgroundColor: theme.palette.background.paper,
    ...(left ? { minWidth: 200 } : { width: 120, textAlign: 'center' })
  });

  const renderStars = (rating) => {
    const numRating = Number(rating) || 0;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {[...Array(numRating)].map((_, i) => (
          <StarIcon
            key={i}
            sx={{
              fontSize: 18,
              color: theme.palette.warning.main
            }}
          />
        ))}
      </Box>
    );
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const truncateReview = (review, maxLength = 100) => {
    if (!review) return 'No review text';
    return review.length > maxLength ? `${review.substring(0, maxLength)}...` : review;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };
  const renderPropertyDetails = (review) => {
    if (!review?.property) {
      return (
        <Typography
          sx={{
            color: palette.text.secondary,
            fontSize: '13px'
          }}
        >
          No Property Attached
        </Typography>
      );
    }

    const property = review?.property;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Property Image */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: palette.grey[200]
          }}
        >
          {property?.main_image ? (
            <img
              src={property.main_image}
              alt={property.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: property.main_image ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.grey[300]
            }}
          >
            <Typography variant="caption" color="textSecondary">
              No Image
            </Typography>
          </Box>
        </Box>

        {/* Property Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={property.title} arrow>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                fontSize: '14px',
                color: palette.text.primary,
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property?.title, 25)}
            </Typography>
          </Tooltip>

          <Tooltip title={property?.address} arrow>
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.text.secondary,
                lineHeight: 1.2,
                mb: 0.5,
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property?.address, 30)}
            </Typography>
          </Tooltip>

          <Typography
            sx={{
              fontSize: '11px',
              color: palette.text.disabled,
              lineHeight: 1.2,
              mt: 1.45
            }}
          >
            {property?.city || 'N/A'} | {property.state || 'N/A'}
          </Typography>
        </Box>
      </Box>
    );
  };
  return (
    <Page title={`Review & Rating | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography
          sx={{
            fontSize: { xs: 20, md: 25 },
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.titledark,
            mb: 5,
            mt: 2
          }}
        >
          Manage Review & Rating
        </Typography>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={7} lg={8}>
            <TextField
              variant="outlined"
              placeholder="Search by user name or review text..."
              size="medium"
              fullWidth
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: tempSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: palette.divider },
                  '&:hover fieldset': { borderColor: palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: palette.primary.main }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2} lg={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="property-select-label">Select Property</InputLabel>

              <Select
                labelId="property-select-label"
                value={selectedProperty}
                label="Select Property"
                onChange={(e) => {
                  setSelectedProperty(e.target.value);
                  setPage(1);
                }}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 350 }
                  }
                }}
                sx={{ fontFamily: 'poppinsMedium', backgroundColor: palette.background.paper }}
              >
                {/* Search Field inside dropdown */}
                <MenuItem disableRipple disableTouchRipple>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search property..."
                    value={propertySearch}
                    onChange={(e) => setPropertySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </MenuItem>

                {/*  "All" option - always show at top */}
                <MenuItem key="all" value="all">
                  All Properties
                </MenuItem>

                {/* Filtered list */}
                {filteredProperties.length > 0 ? (
                  filteredProperties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property?.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No property found</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              fullWidth
              sx={{ height: 56, fontSize: '16px', fontFamily: 'poppinsMedium' }}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': { height: 8, width: 8 },
                '&::-webkit-scrollbar-track': { background: palette.grey[200] },
                '&::-webkit-scrollbar-thumb': {
                  background: palette.grey[400],
                  borderRadius: 2
                }
              }}
            >
              <Table
                sx={{
                  minWidth: 900,
                  '& .MuiTableCell-head': {
                    backgroundColor: theme.palette.background.paper,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsSemiBold',
                    fontSize: '14px',
                    borderBottom: `2px solid ${palette.divider}`,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    color: palette.text.primary,
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    borderBottom: `1px solid ${palette.divider}`,
                    py: 2
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          whiteSpace: 'nowrap',
                          px: 2,
                          position:
                            header === 'User Details' || header === 'Actions'
                              ? { xs: 'relative', md: 'sticky' }
                              : 'relative',
                          backgroundColor: theme.palette.background.paper,
                          zIndex:
                            header === 'User Details' || header === 'Actions'
                              ? { xs: 'auto', md: 2 }
                              : 'auto',
                          ...(header === 'User Details' && stickyStyle(true)),
                          ...(header === 'Actions' && stickyStyle(false))
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {tableHeaders.map((_, j) => (
                          <TableCell key={j} sx={{ px: 2 }}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : reviews.length > 0 ? (
                    reviews.map((review) => (
                      <TableRow
                        key={review.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ px: 2, minWidth: 200 }}>
                          {renderPropertyDetails(review)}
                        </TableCell>
                        <TableCell sx={stickyStyle(true)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box>
                              <Typography sx={{ fontFamily: 'poppinsMedium', fontSize: '14px' }}>
                                {review?.name || 'N/A'}
                              </Typography>
                              <Typography
                                sx={{ color: palette.text.secondary, fontSize: '13px', mt: 0.5 }}
                              >
                                {review?.email || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ px: 2, minWidth: 300 }}>
                          <Typography
                            sx={{
                              fontFamily: 'poppinsRegular',
                              fontSize: '14px',
                              lineHeight: 1.5
                            }}
                          >
                            {truncateReview(review.review)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Chip
                            label={review.status}
                            color={getStatusColor(review.status)}
                            size="small"
                            variant="filled"
                          />
                        </TableCell>
                        {/* <TableCell sx={{ px: 2, minWidth: 300 }}>
                          <Typography
                            sx={{
                              fontFamily: 'poppinsRegular',
                              fontSize: '14px',
                              cursor: 'pointer',
                              lineHeight: 1.5
                            }}
                          >
                            <a
                              href={review.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              {review.website || '-'}
                            </a>
                          </Typography>
                        </TableCell> */}
                        <TableCell sx={{ px: 2 }}>{renderStars(review?.rating)}</TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Typography sx={{ fontFamily: 'poppinsRegular', fontSize: '13px' }}>
                            {review.published_at || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2, textAlign: 'center' }}>
                          <Switch
                            checked={review.status === 'active'}
                            onChange={() => handleStatusToggle(review)}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={stickyStyle(false)}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="View Review">
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(review)}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Review">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(review)}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No reviews found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {!loading && pagination.total > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <Typography sx={{ fontFamily: 'poppinsRegular', color: palette.text.secondary }}>
              {/* Showing {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} reviews */}
            </Typography>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1
            }
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              id="delete-dialog-description"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
            >
              Are you sure you want to delete review from <strong>{selectedReview?.name}</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                '&:hover': {
                  borderColor: palette.grey[600],
                  backgroundColor: palette.action.hover
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                '&:hover': {
                  backgroundColor: palette.error.dark
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <Skeleton variant="circular" width={20} height={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Review Modal - External Component */}
        <ViewReviewModal open={viewModalOpen} onClose={handleViewClose} review={viewReview} />
      </Box>
    </Page>
  );
}
